import { FC, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Divider, Input, Modal, Row } from 'antd';
import { TbArrowDown, TbArrowRight } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    AttachmentDestination,
    CloudAttachment,
    CloudStorageConnection,
    createCloudAttachmentFolder,
} from '@/services';
import { CloudStorageConnectionTrigger, SelectDataSourceDropdown } from '@/shared/components/dropdowns';
import { PlatformForm as Form } from '@/shared/components/forms';
import { WorkspaceFolderGlyph } from '@/shared/components/workspace-resources';

import ResourceCreationFormLabel from '../components/ResourceCreationFormLabel';
import ResourceCreationModalContent from '../components/ResourceCreationModalContent';

interface CreateAttachmentFolderModalProps {
    destination: AttachmentDestination;
    open: boolean;
    selectedConnection: Partial<CloudStorageConnection>;
    onCancel: () => void;
    onCreated: (connection: CloudStorageConnection, folder: CloudAttachment) => void;
}

const isValidFolderName = (value: string) => {
    const normalized = value.trim();

    return Boolean(
        normalized && normalized !== '.' && normalized !== '..' && !/[\\/\u0000-\u001f\u007f]/.test(normalized),
    );
};

const CreateAttachmentFolderModal: FC<CreateAttachmentFolderModalProps> = ({
    destination,
    open,
    selectedConnection,
    onCancel,
    onCreated,
}) => {
    const { token } = useAntDesignTheme();
    const [connection, setConnection] = useState<Partial<CloudStorageConnection>>(selectedConnection);
    const [folderName, setFolderName] = useState('');
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const valid = Boolean(connection.id && connection.type && isValidFolderName(folderName));

    const close = () => {
        if (savingRef.current) {
            return;
        }

        setFolderName('');
        onCancel();
    };

    const createFolder = async () => {
        if (!connection.id || !connection.type || !isValidFolderName(folderName) || savingRef.current) {
            return;
        }

        const selected = { id: connection.id, type: connection.type, label: connection.label };

        try {
            savingRef.current = true;
            setSaving(true);
            const folder = await createCloudAttachmentFolder(selected, destination, folderName.trim());

            onCreated(selected, folder);
            setFolderName('');
            onCancel();
            toast.success('Folder created', { description: folder.name });
        } catch (error) {
            captureException(error);
            toast.error('Unable to create attachment folder');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return (
        <Modal
            afterOpenChange={(nextOpen) => {
                if (nextOpen) {
                    setConnection(selectedConnection);
                    setFolderName('');
                    savingRef.current = false;
                    setSaving(false);
                }
            }}
            footer={
                <Row style={{ width: '100%' }}>
                    <Col flex='none'>
                        <Button disabled={saving} size='middle' onClick={close}>
                            Cancel
                        </Button>
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <Button
                            disabled={!valid}
                            loading={saving}
                            size='middle'
                            type='primary'
                            onClick={() => void createFolder()}
                        >
                            Create Folder
                            <TbArrowRight />
                        </Button>
                    </Col>
                </Row>
            }
            mask={false}
            open={open}
            closable={!saving}
            title='Create Folder in Attachments'
            onCancel={close}
        >
            <ResourceCreationModalContent>
                <Form layout='vertical' onFinish={() => void createFolder()}>
                    <Form.Item label='Cloud Storage Provider' style={{ marginBottom: 0 }}>
                        <SelectDataSourceDropdown
                            block
                            disabled={saving}
                            matchTriggerWidth
                            selectedConnection={connection}
                            setSelectedConnection={setConnection}
                        >
                            <CloudStorageConnectionTrigger block disabled={saving} selectedConnection={connection} />
                        </SelectDataSourceDropdown>
                    </Form.Item>
                    <Divider style={{ marginBottom: '0.5rem' }}>
                        <Avatar
                            icon={<TbArrowDown size='75%' style={{ color: token.colorTextTertiary }} />}
                            shape='square'
                            size='small'
                            style={{ border: `1px solid ${token.colorBorder}` }}
                        />
                    </Divider>
                    <Form.Item
                        label={
                            <ResourceCreationFormLabel help='Names cannot contain slashes or control characters.'>
                                Folder Name
                            </ResourceCreationFormLabel>
                        }
                        style={{ marginBottom: 0 }}
                        validateStatus={folderName && !isValidFolderName(folderName) ? 'error' : undefined}
                    >
                        <Input
                            disabled={saving}
                            maxLength={128}
                            placeholder='Enter A Folder Name...'
                            prefix={<WorkspaceFolderGlyph style={{ marginRight: '0.25rem' }} variant='create' />}
                            value={folderName}
                            onChange={(event) => setFolderName(event.target.value)}
                        />
                    </Form.Item>
                </Form>
            </ResourceCreationModalContent>
        </Modal>
    );
};

export default CreateAttachmentFolderModal;
